import { useEffect, useMemo, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO, setCurrentStageName } from '../../reducers/rtfoSlice';

import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import axios from 'axios';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOHistoricPumpdownChart = ({ gridView, selectedStages, pumpdownHistoryTraces, pumpdownHistoryNormalizeBy }) => {
  
  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const dispatch = useDispatch();

  const currentStageName = currentRTFO?.currentStageName;
  const currentStage = currentRTFO?.stageData?.[currentStageName];

  const darkMode = user?.value?.darkMode;
  const units = currentRTFO?.settings?.units || 'metric';

  const [pumpdowns, setPumpdowns] = useState({});

  useEffect(() => {
    
    // if (!selectedStages?.length > 0 || Object.keys(pumpdowns)?.length > 0) return;

    fetchAllPumpdowns();
    
    async function fetchAllPumpdowns() {
      for (const stageName of selectedStages) {
        if (pumpdowns[stageName]) continue;

        const timestamp = new Date().getTime();
        try {
          const pumpdownData = (await axios.get(`https://ssd-pumpdowndata.s3.us-east-2.amazonaws.com/${stageName}_pumpdown_data.json?timestamp=${timestamp}`))?.data;
          setPumpdowns(prev => ({...prev, [stageName]: pumpdownData}));
        } catch (error) {
          console.log(error);
        };
      };
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStages]);


  const data = useMemo(() => {
    let traces = [];

    // add all background traces
    selectedStages?.filter(name => name !== currentStageName)?.forEach(stageName => {
      traces = traces.concat(addTraces(stageName));
    });
    
    // add the current trace on top
    traces = traces.concat(addTraces(currentStageName));

    function addTraces(stageName) {
      try {
        const stage = currentRTFO?.stageData?.[stageName]; 
        const data = pumpdowns?.[stageName];

        if (data && data.length > 0) {
         
          let X = [];
          let YPressure = [];
          let YRate = [];
          let YDepth = [];
          
          const maxDepth = Math.max(...data?.map(d => d.depth || 0));

          const startTimestamp = 
            pumpdownHistoryNormalizeBy === 'depth' ? 
            data?.find(d => d.depth === maxDepth)?.timestamp :
            stage?.pumpdownParams?.pressureTestTimestamps?.endTimestamp || stage?.pumpdownParams?.postPlugFalloffTimestamps?.startTimestamp || data?.[0]?.timestamp;

          data?.forEach(d => {
            let time = (d.timestamp - startTimestamp) / 60000;
            X.push(time);
            YRate.push((d.rate * (units === 'field' ? rtfoConversionFactors.rate : 1)) || null);
            YPressure.push(((d.pressure || (d.leftPumpPressure + d.rightPumpPressure) / 2) * (units === 'field' ? rtfoConversionFactors.pressure : 1)) || null);
            YDepth.push((d.depth * (units === 'field' ? rtfoConversionFactors.depth : 1)) || null);
          });

          const rateTrace = { // rate
            x: X,
            y: YRate,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y2',
            name: `${stage?.name?.split("_")?.slice(1)?.join("_")}-Rate (${(units === 'field' ? 'bbl/min' : 'm3/min')})`,
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[3],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5),
            connectgaps: false,
            hovertemplate: `<b>Stage: ${stage?.name}</b><br>Time: %{x:.2f} min<br>Rate: %{y:.2f} ${(units === 'field' ? 'bbl/min' : 'm3/min')}<extra></extra>`
          };
          
          const pressureTrace = { // pressure
            x: X,
            y: YPressure,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: `${stage.name?.split("_")?.slice(1)?.join("_")}-Pressure (${(units === 'field' ? 'psi' : 'MPa')})`,
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[2],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5),
            connectgaps: false,
            hovertemplate: `<b>Stage: ${stage?.name}</b><br>Time: %{x:.2f} min<br>Pressure: %{y:.2f} ${(units === 'field' ? 'psi' : 'MPa')}<extra></extra>`
          };

          const depthTrace = { // depth
            x: X,
            y: YDepth,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y3',
            name: `${stage.name?.split("_")?.slice(1)?.join("_")}-Depth (${(units === 'field' ? 'ft' : 'm')})`,
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: 'black',
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5),
            connectgaps: false,
            hovertemplate: `<b>Stage: ${stage?.name}</b><br>Time: %{x:.2f} min<br>Depth: %{y:.2f} ${(units === 'field' ? 'ft' : 'm')}<extra></extra>`
          };

          const stageTraces = [];

          if (pumpdownHistoryTraces?.indexOf('rate') !== -1) {
            stageTraces.push(rateTrace);
          } else {
            stageTraces.push({...rateTrace, x: [], y: []});
          };
          if (pumpdownHistoryTraces?.indexOf('pressure') !== -1) {
            stageTraces.push(pressureTrace);
          } else {
            stageTraces.push({...pressureTrace, x: [], y: []});
          };
          if (pumpdownHistoryTraces?.indexOf('depth') !== -1) {
            stageTraces.push(depthTrace);
          } else {
            stageTraces.push({...depthTrace, x: [], y: []});
          };

          return stageTraces;
        } else {
          return [];
        };
      } catch (error) {
        console.log(error);
        return [];
      };
    };

    return traces;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pumpdowns, selectedStages, pumpdownHistoryNormalizeBy, pumpdownHistoryTraces, units]);

  const layout = useMemo(() => {
    return {
     // grid: {rows: 3, columns: 1, pattern: 'independent'},
      yaxis: { // Treating pressure y axis
        linecolor: chartColors[2],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[2],
        title: {
          text: `Pumpdown Pressure (${(units === 'field' ? 'psi' : 'MPa')})`,
          font: {
            size: 12
          }
        },
        anchor: 'x',
        range: units === 'field' ? [0, 15000] : [0, 100],
        // domain: [0.7, 1],
        side: 'left'
      },
      yaxis2: { // Slurry rate y axis
        linecolor: chartColors[3],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[3],
        title: {
          text: `Pumpdown Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          font: {
            size: 12
          },
        },
        range: [0, 25],
        anchor: 'free',
        overlaying: 'y',
        side: 'left',
        position: 0
      },
      yaxis3: { // Prop conc surf y axis
        linecolor: 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: 'black',
        title: {
          text: `Depth (${units === 'field' ? 'ft' : 'm'})`,
          font: {
            size: 12
          }
        },
        // range: [0, 1000],
        overlaying: 'y',
        side: 'right',
        anchor: 'x',
      },
      xaxis: { // x axis
        title: {
          text: 'Time (min)',
          font: {
            size: 12
          }
        },
        domain: [(gridView ? 0.1 : 0.05), 1],
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        anchor: 'y',
        showspikes: false,
        spikethickness: 2,
        spikedash: "dot",
        spikecolor: "#999999",
        spikemode: "across"
      },
      autosize: true,
      showlegend: false,
      legend: {
        orientation: "h",
        yanchor: "top",
        font: {
          color: darkMode ? 'lightgrey' : 'grey'
        },
        y: -0.2,
      },
      annotations: [
        {
          text: "Pumpdown History",
            font: {
            size: 14,
              color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.125 : 1.075), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
        {
          text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
            font: {
            size: 10,
              color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.075 : 1.035), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
      ],
      uirevision: true,
      margin: {"t": 60, "l": 60, "r": 60, "b": 60},
      images: [
        {
          x: (gridView ? 0.05 : 0.035),
          y: (gridView ? 1.05 : 1.025),
          sizex: (gridView ? 0.1 : 0.05),
          sizey: 0.1,
          source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
          xref: "page",
          yref: "y1 domain",
          xanchor: "right",
          yanchor: "bottom",
          opacity: 0.75
        }
      ],
      plot_bgcolor: "transparent",
      paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
    };

  }, [darkMode, units, gridView, currentRTFO?.name, currentStage?.wellName, currentStage?.stageNumber, theme?.palette?.elevationOne]);

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 480,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
      onClick={handleChartClick}
    />
  );

  function handleChartClick(e) {
    try {
      const traceClicked = e.points?.[0]?.data?.name;
      const wellName = traceClicked?.split('_')?.[0];
      let stageNumber = traceClicked?.split('_')?.[1];
      stageNumber = stageNumber?.split('-')?.[0];
      const newStageName = `${currentRTFO?.name}_${wellName}_${stageNumber}`;

      if (newStageName && currentRTFO?.stageData?.[newStageName] && !currentRTFO?.isLoadingData) {
        dispatch(setCurrentStageName(newStageName));
      };
    } catch (error) {
      console.log(error);
    };
  };
};
